import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AuthenticatedUser } from "../../lib/api";
import { logout } from "../../lib/api";
import { ProjectsPanel } from "./ProjectsPanel";
import { UsersPanel } from "./UsersPanel";

/**
 * The shell: who is signed in, sign out, and the two things an administrator
 * manages. Not tabs, deliberately — the catalog is small enough that seeing
 * both at once beats a click to switch between them, and a person's group
 * often needs to be read against which projects currently exist.
 */
export function AdminDashboard({ user }: { user: AuthenticatedUser }) {
  const queryClient = useQueryClient();

  const signOut = useMutation({
    mutationFn: logout,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["session"] }),
  });

  return (
    <div className="min-h-dvh bg-adriatic-deep/5">
      <header className="border-b border-adriatic/15 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div className="flex items-center gap-3">
            <img
              src="/brand/logo.svg"
              alt="Acquire Intelligence"
              width={120}
              height={33}
              className="h-8 w-auto"
            />
            <span className="text-sm font-semibold text-adriatic-deep">AI Labs — Admin</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-adriatic-mid">
            <span className="break-all">{user.name ?? user.email}</span>
            <button
              type="button"
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
              className="rounded-full border border-adriatic/25 px-3 py-1.5 font-medium text-adriatic-deep transition-colors hover:bg-adriatic-deep/5 focus-visible:ring-2 focus-visible:ring-acqua/50 focus-visible:outline-none disabled:opacity-60"
            >
              {signOut.isPending ? "Signing out..." : "Sign out"}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-6 py-8">
        <ProjectsPanel />
        <UsersPanel />
      </main>
    </div>
  );
}
